import AppButton from '@nxsys/components/AppButton';
import * as Icons from '@nxsys/components/AppIconSvg';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  useDeleteDraftPayrollById,
  useGetDraftPayroll,
} from '@nxsys/hooks/api/payroll';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { payrollActions } from '@nxsys/store/slices/payroll/reducer/payrollSlice';
import { GetDraftPayrollParams } from '@nxsys/store/slices/payroll/type';
import { showMessage } from '@nxsys/utils/message';
import { ExportToCsv } from 'export-to-csv';
import { DraftPayroll } from '@nxsys/types/models/payroll';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { getDraftPayrollErrorColumns } from '../listing/column';
import NmwBreakdownModal from './NmwBreakdownModal';

type DraftPayrollErrorModalProps = {
  isOpen: boolean;
  toggle: () => void;
};

const DraftPayrollErrorModal: React.FC<DraftPayrollErrorModalProps> = ({
  isOpen,
  toggle,
}: DraftPayrollErrorModalProps) => {
  const { messages } = useTypedIntl();
  const theme = useTheme();
  const dispatch = useDispatch();

  const {
    draftPayroll: { list, pagination, queryParams, loading },
  } = useSelector((state: AppState) => state.payrollStore);

  const { isBureau } = useSelector((state: AppState) => state.appStore);

  const [nmwBreakdownData, setNmwBreakdownData] = useState<DraftPayroll | null>(null);
  const handleNmwBreakdownClick = useCallback((data: DraftPayroll) => {
    setNmwBreakdownData(data);
  }, []);

  const { run: getDraftPayroll, loading: isGetDraftPayrollLoading } =
    useGetDraftPayroll();

  const {
    run: deleteDraftPayrollById,
    loading: isDeleteDraftPayrollByIdLoading,
  } = useDeleteDraftPayrollById();

  useEffect(() => {
    if (isOpen)
      handleGetDraftPayroll({
        page: 0,
        size: 20,
      });
  }, [isOpen]);

  const handleGetDraftPayroll = (params: GetDraftPayrollParams) => {
    const newParams = {
      ...params,
      hasError: true,
    };
    dispatch(payrollActions.getDraftPayrollList(newParams));
  };

  const refetchDraftPayroll = () => {
    handleGetDraftPayroll({
      ...queryParams,
    });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleGetDraftPayroll({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleExportAll = async () => {
    if (!pagination.totalDocs) {
      showMessage({ type: 'warning', content: messages['common.noDataToExport'] });
      return;
    }
    const res = await getDraftPayroll({
      page: 0,
      size: pagination.totalDocs,
      hasError: true,
    });
    const data = res.data;
    const dataExport = data.map((item) => ({
      employeeID: item?.subcontractorRef?.id || '',
      employeeName:
        item?.subcontractorRef?.fullName ||
        `${item?.subcontractorRef?.forename || ''} ${
          item?.subcontractorRef?.surname || ''
        }`,
      ...(!isBureau
        ? {
            agencyRef: `${
              item?.subcontractorRef?.primaryAgency?.agencyPrefix || '___'
            }/${item?.subcontractorRef?.agencyRef || ''}`,
          }
        : {}),
      paymentCompany: item?.subcontractorRef?.companyPayment?.companyName || '',
      payFrequency: item?.payFrequency || '',
      ...(!isBureau ? { agency: item?.agencyRef.name || '' } : {}),
      errorType: !isEmpty(item?.errors) ? item?.errors[0]?.type : '',
      errorMessage: !isEmpty(item?.errors) ? item?.errors[0]?.message : '',
    }));
    const headers = [
      messages['common.employeeId'],
      messages['common.employeeName'],
      ...(!isBureau ? [messages['common.agencyRef']] : []),
      isBureau ? messages['common.employer'] : messages['common.paymentCompany'],
      messages['common.payFrequency'],
      ...(!isBureau ? [messages['common.agencyName']] : []),
      messages['common.errorType'],
      messages['common.errorMessage'],
    ];
    const csvExporter = new ExportToCsv({
      filename:
        `Errors-export-${moment(new Date())
          .tz('Europe/London')
          .format('DD/MM/YYYY')}` || 'errors.csv',
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    csvExporter.generateCsv(dataExport);
  };

  const handleDelete = (id: string) => {
    deleteDraftPayrollById(id).then((res) => {
      if (res.success) {
        showMessage({
          type: 'success',
          content: messages['timesheet.draftPayroll.deleteSuccess'],
        });
        refetchDraftPayroll();
      }
    });
  };

  return (
    <>
    <AppModalContainer
      title={messages['common.errors']}
      isOpen={isOpen}
      toggle={toggle}
      width={1000}
      minHeight="0px"
      noPaddingBody
    >
      <div className="flex justify-end my-4 px-4">
        <AppButton
          name={messages['common.exportAll']}
          leftIcon={<Icons.IconArrowExport color="#fff" />}
          onClick={handleExportAll}
          loading={isGetDraftPayrollLoading}
          disabled={isGetDraftPayrollLoading}
        />
      </div>
      <AppTableContainer
        rowKey="id"
        data={list ?? []}
        loading={loading || isDeleteDraftPayrollByIdLoading}
        page={pagination.current}
        total={pagination.totalDocs}
        pageSize={pagination.pageSize}
        scroll={{ x: 1000, y: 700 }}
        titleEmpty={messages['timesheet.draftPayroll.empty']}
        columns={getDraftPayrollErrorColumns({
          messages,
          onDelete: handleDelete,
          theme,
          isBureau,
          onNmwBreakdownClick: handleNmwBreakdownClick,
        })}
        onChangePagination={onChangePagination}
      />
    </AppModalContainer>
    <NmwBreakdownModal
      isOpen={!!nmwBreakdownData}
      toggle={() => setNmwBreakdownData(null)}
      data={nmwBreakdownData}
    />
    </>
  );
};

export default DraftPayrollErrorModal;
